/**
 * ScreeningView
 * - Pure UI (no API calls)
 * - Receives state + handlers from Screening
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import PaginationControls from "../../../../utils/PaginationControls";
import { StatusBadgeSm } from "../../../../utils/StatusBadge";
import TagFilterPanel from "./TagFilterPanel";
import YearRangePicker from "../../../../utils/YearRangePicker";

/* A native <select> renders its list through the OS and ignores the app's
   styling, so this is a toggle button plus a floating card of options: light
   rows, the selected one filled blue, at most 20vh tall and scrollable.
   Closes on an outside click or Esc. Same as AbstractsList. */
const InlineSelect = ({ id, openId, setOpenId, value, options = [], placeholder = "Select", width, className = "" }) => {
  const [localOpen, setLocalOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const controlled = typeof setOpenId === "function";
  const isOpen = controlled ? openId === id : localOpen;
  const setOpen = (next) => {
    if (controlled) setOpenId(next ? id : null);
    else setLocalOpen(next);
  };
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Bring the selected option into view inside the list (without
    // scrolling the filter panel itself).
    const list = listRef.current;
    const active = list?.querySelector('[data-selected="true"]');
    if (list && active) {
      list.scrollTop = active.offsetTop - list.clientHeight / 2 + active.offsetHeight / 2;
    }

    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div ref={rootRef} className={`position-relative ${className}`} style={width ? { width } : undefined}>
      <button
        type="button"
        className="ev-btn-light-fill ev-btn-small ev-radius-xsmall w-100 d-flex align-items-center justify-content-between"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setOpen(!isOpen)}
      >
        <span className="text-truncate">{selected ? selected.label : placeholder}</span>
        <i className={`bi bi-chevron-${isOpen ? "up" : "down"} ms-1`}></i>
      </button>
      {isOpen && (
        <div
          ref={listRef}
          role="listbox"
          className="position-absolute start-0 mt-1 p-2 bg-white border border-primary ev-radius-xsmall shadow-sm d-flex flex-column gap-1 scrollable-element"
          style={{
            top: "100%",
            zIndex: 1000,
            minWidth: "100%",
            width: "max-content",
            maxHeight: "20vh",
            overflowY: "auto",
          }}
        >
          {options.map((opt) => {
            const isSelected = value === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-selected={isSelected ? "true" : undefined}
                className={`${isSelected ? "ev-btn-blue-fill-round" : "ev-btn-light-fill"} ev-btn-small ev-radius-xsmall w-100 text-start text-nowrap flex-shrink-0`}
                onClick={() => { opt.onSelect?.(); setOpen(false); }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ADV_FIELD_OPTIONS = [
  { value: "", label: "Field" },
  { value: "title", label: "Title" },
  { value: "abstract", label: "Abstract" },
  { value: "author", label: "Author" },
  { value: "year", label: "Year" },
  { value: "population", label: "Population" },
  { value: "intervention", label: "Intervention" },
  { value: "comparison", label: "Comparator" },
  { value: "outcome", label: "Outcome" },
  { value: "study_design", label: "Study Design" },
  { value: "exclusion", label: "Exclusion" },
];

const ADV_OPERATOR_OPTIONS = [
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Does not contain" },
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Does not equal" },
  { value: "begins_with", label: "Starts with" },
  { value: "not_begins_with", label: "Does not start with" },
  { value: "ends_with", label: "Ends with" },
  { value: "not_ends_with", label: "Does not end with" },
  { value: "is_empty", label: "Is empty" },
  { value: "is_not_empty", label: "Is not empty" },
  { value: "is_null", label: "Is null" },
  { value: "is_not_null", label: "Is not null" },
];

const EMPTY_ID_SET = new Set();

const ScreeningView = ({
  mode,
  title,
  isAI,
  isQC,
  isQC_FT,
  isConflict,
  isConflict_FT,
  isFullTextMode,

  picoColors,
  tagOptions,
  selectedTags,
  onToggleTag,

  panelOpen,
  setPanelOpen,
  navData,
  counts,
  metrics,
  loading,
  listData,
  pagination,
  page,
  setPage,
  pageSize,
  onPageSizeChange,

  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  decisionFilter,
  setDecisionFilter,
  reviewerFilter,
  setReviewerFilter,
  reasonFilter = null,
  setReasonFilter,

  // Search props
  searchInput,
  setSearchInput,
  onSearch,
  onSearchKeyPress,

  setActiveCardId,

  // Panel navigation (item-to-item in review panel)
  onPanelPrev,
  onPanelNext,
  // Page navigation (list pagination)
  onPagePrev,
  onPageNext,

  onRunAI,
  onRestartAI,
  onStopAI,
  onDownloadQC,
  onAddPapers,
  addPaperStatus = null,
  isAddingPapers = false,
  addPaperProgress = { done: 0, total: 0 },
  isQCCompleted,
  onMarkComplete,
  onRefresh,
  onPreviewPdf,
  onOpenPanel,

  showSaveButton,
  canSave,
  isSaving,
  onSaveClick,

  panelContent,
  isRunDisabled,
  aiStatus,
  isDownloadingQC,
  hasAI,
  confidenceStart,
  setConfidenceStart,
  confidenceEnd,
  setConfidenceEnd,
  filtersCollapsed,
  setFiltersCollapsed,
  // Advanced filter state (lifted to Screening)
  advancedEnabled,
  setAdvancedEnabled,
  advConditions,
  setAdvConditions,
  onApplyAdvancedFilters,
  // Bulk actions
  onBulkDecision,
  criteriaList = [],
  isBulkLoading,
  activeFilterCount = 0,
  onClearAllFilters,

  // NEW: Export / Import screening template (Human screening only)
  onOpenExportModal,
  onImportFile,
  isExporting,
  isImporting,
  readOnly = false,
  // QC lock: AI run/restart blocked for everyone; decisions read-only for non-creators
  aiLockedByQC = false,
  aiLockMessage = "",
  qcLockMessage = null,

  // Creator reviewer-set toggle (DUAL_HUMAN_HUMAN only)
  isOwner = false,
  framework = null,
  reviewerSet = "SET_1",
  onReviewerSetChange = null,

  // study_ids of papers added via Full-Text QC → Add Paper ("Bibliographic")
  bibliographicIds = EMPTY_ID_SET,
}) => {
  const addPaperInputRef = useRef(null);

  // Filter is hidden when there are no bibliographic papers — never leave it stuck on BIBLIOGRAPHIC
  useEffect(() => {
    if (bibliographicIds.size === 0 && decisionFilter === "BIBLIOGRAPHIC") setDecisionFilter(null);
  }, [bibliographicIds, decisionFilter]); // eslint-disable-line

  const [localConfStart, setLocalConfStart] = useState(
    confidenceStart !== null ? confidenceStart : ""
  );
  const [localConfEnd, setLocalConfEnd] = useState(
    confidenceEnd !== null ? confidenceEnd : ""
  );

  // Filter accordion open states
  const [section1Open, setSection1Open] = useState(false);
  const [section2Open, setSection2Open] = useState(false);
  const [section3Open, setSection3Open] = useState(false);
  const [agentScopeOpen, setAgentScopeOpen] = useState(false);
  const [reasonDropdownOpen, setReasonDropdownOpen] = useState(false);
  // Which advanced-filter dropdown is expanded, keyed "<ruleId>:<which>".
  const [advOpenDropdown, setAdvOpenDropdown] = useState(null);
  // reasonFilter === null means both "nothing picked yet" and "All Reasons";
  // this tells them apart so "All Reasons" isn't shown as chosen up front.
  const [reasonChosen, setReasonChosen] = useState(false);
  const [pendingReviewerFilter, setPendingReviewerFilter] = useState(null); // agent chosen but reason not yet picked

  // Bulk decision dropdown state
  const [bulkDropdownOpen, setBulkDropdownOpen] = useState(null); // null | 'INCLUDED' | 'EXCLUDED'
  const [selectedBulkCriteriaIds, setSelectedBulkCriteriaIds] = useState([]);
  const bulkDropdownRef = React.useRef(null);

  // Multi-AI: pending decision filter — set when Included/Excluded clicked with >1 agent
  // Actual decisionFilter is only applied after user picks an agent from the dropdown
  const [pendingDecisionFilter, setPendingDecisionFilter] = useState(null);

  // Checkbox selection for bulk actions
  const [selectedCardIds, setSelectedCardIds] = useState(new Set());
  const [isMarkedComplete, setIsMarkedComplete] = useState(false);

  // NEW: ref for the hidden file input used by "Import Decisions"
  const importFileInputRef = React.useRef(null);

  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (file) onImportFile?.(file);
  };

  // Close bulk dropdown on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (bulkDropdownRef.current && !bulkDropdownRef.current.contains(e.target)) {
        setBulkDropdownOpen(null);
        setSelectedBulkCriteriaIds([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleBulkCriteria = (id) => {
    setSelectedBulkCriteriaIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkConfirm = () => {
    if (!selectedBulkCriteriaIds.length || selectedCardIds.size === 0) return;
    const ids = Array.from(selectedCardIds);
    onBulkDecision?.(bulkDropdownOpen, selectedBulkCriteriaIds, ids);
    setBulkDropdownOpen(null);
    setSelectedBulkCriteriaIds([]);
    setSelectedCardIds(new Set());
  };

  // Clear card selections when list/page/filters change
  useEffect(() => {
    setSelectedCardIds(new Set());
  }, [listData, page, decisionFilter, searchInput, reviewerFilter]);

  // Reset agent ref and pending states when mode changes (new screening context)
  useEffect(() => {
    aiAgentsRef.current = [];
    setPendingReviewerFilter(null);
  }, [mode]); // eslint-disable-line

  // Derive AI agents (actual agent_ids + labels) from listData
  const aiAgentsRef = React.useRef([]);
  const aiAgents = React.useMemo(() => {
    if (!Array.isArray(listData) || listData.length === 0) return aiAgentsRef.current;
    let best = [];
    for (const item of listData) {
      const decisions = item.ai_decisions || [];
      if (decisions.length > best.length) best = decisions;
    }
    const computed = best.map((d, i) => ({
      agent_id: d.agent_id,
      label: `Agent ${i + 1}`,
    }));
    if (computed.length > 0) aiAgentsRef.current = computed;
    return computed;
  }, [listData]);

  const aiReviewerCount = aiAgents.length;

  // advancedEnabled, advConditions are now props from Screening

  const addAdvCondition = () =>
    setAdvConditions((prev) => [
      ...prev,
      { id: Date.now(), category: "", filterOp: "contains", connector: "AND", value: "" },
    ]);
  const removeAdvCondition = (id) =>
    setAdvConditions((prev) => prev.filter((c) => c.id !== id));
  const updateAdvCondition = (id, field, val) =>
    setAdvConditions((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: val } : c))
    );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localConfStart === "" || localConfStart === null) {
        setConfidenceStart(null);
      } else {
        const num = parseFloat(localConfStart);
        if (!isNaN(num) && num >= 0 && num <= 100) {
          setConfidenceStart(num);
        }
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [localConfStart]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localConfEnd === "" || localConfEnd === null) {
        setConfidenceEnd(null);
      } else {
        const num = parseFloat(localConfEnd);
        if (!isNaN(num) && num >= 0 && num <= 100) {
          setConfidenceEnd(num);
        }
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [localConfEnd]);

  useEffect(() => {
    if (confidenceStart === null) {
      setLocalConfStart("");
    }
  }, [confidenceStart]);

  useEffect(() => {
    if (confidenceEnd === null) {
      setLocalConfEnd("");
    }
  }, [confidenceEnd]);

  console.log(aiStatus);
  const MetricCard = ({ value, label, valueClassName, showDivider }) => (
    <>
      {showDivider && <div className="vr my-2"></div>}
      <div className="d-flex flex-column align-items-center text-center flex-grow-1 py-2">
        <span className={`ev-bold-large ${valueClassName || ""}`}>{value}</span>
        <span className={`ev-bold-reg ${valueClassName || ""}`}>{label}</span>
      </div>
    </>
  );

  const getPicoTypeForTag = (tag, picoJson = {}) => {
    const lowerTag = String(tag || "").toLowerCase();

    for (const [type, keywords] of Object.entries(picoJson)) {
      if (!Array.isArray(keywords)) continue;
      const match = keywords.find((k) =>
        lowerTag.includes(String(k).toLowerCase()),
      );
      if (match) return type.toLowerCase();
    }
    return null;
  };

  const PicoTag = ({ text, picoJson, isSelected, onClick }) => {
    const picoType = getPicoTypeForTag(text, picoJson);
    const color = picoColors?.[picoType];

    return (
      <button
        type="button"
        className="ev-btn-xsmall ev-bold-reg py-1 px-2 ev-radius-large border-0"
        onClick={onClick}
        title={text}
        style={{
          background: color ? `${color}${isSelected ? "44" : "22"}` : "#f2f3f8",
          color: color || "#333",
          border:
            isSelected && color
              ? `1px solid ${color}`
              : "1px solid transparent",
        }}
      >
        {text}
      </button>
    );
  };

  // Whether to show checkboxes on list cards (bulk mode active)
  const showBulkCheckboxes = (mode === "AI" || mode === "AI_FT") ||
    (mode === "HUMAN" || mode === "HUMAN_FT");

  const toggleCardSelection = (id) => {
    setSelectedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageCardIds = useMemo(() => {
    if (!Array.isArray(listData)) return [];
    return listData.map((item) => {
      if (mode === "CONFLICT_FT" || mode === "QC_FT") return item.screening_id || item.id;
      if (mode === "HUMAN_FT" || mode === "AI_FT") return item.full_text_screening_id;
      return item.study_id || item.id;
    }).filter(Boolean);
  }, [listData, mode]);

  // Stage QC done: AI and Human screening become a read-only view with a
  // single "completed" note and only the Download Results action.
  const stageCompleted = !!aiLockedByQC;
  const isScreeningMode = ["AI", "AI_FT", "HUMAN", "HUMAN_FT"].includes(mode);
  const completedOnly = stageCompleted && isScreeningMode;
  const completedText = isFullTextMode
    ? "Full-text screening is completed"
    : "Abstract screening is completed";

  const isAllPageSelected = pageCardIds.length > 0 && pageCardIds.every((id) => selectedCardIds.has(id));

  const toggleSelectAll = () => {
    if (isAllPageSelected) {
      setSelectedCardIds(new Set());
    } else {
      setSelectedCardIds(new Set(pageCardIds));
    }
  };

  const buildRestartFilterSummary = () => {
    const parts = [];
    if (searchInput && searchInput.trim()) parts.push(`Search: "${searchInput.trim()}"`);
    if (decisionFilter) parts.push(decisionFilter === "BIBLIOGRAPHIC" ? "Bibliographic papers" : `Decision: ${decisionFilter}`);
    if (dateFrom) parts.push(`From: ${dateFrom}`);
    if (dateTo) parts.push(`To: ${dateTo}`);
    if (selectedTags && selectedTags.length > 0)
      parts.push(`Tags: ${selectedTags.length} selected`);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  return (
    <div className="h-100 d-flex flex-column">
      {/* ================= HEADER ================= */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div
          className={`d-flex gap-4 align-items-center ${!panelOpen ? "w-70" : "w-20"}`}
        >
          <p
            className={`ev-med-p-blue ev-bold-med mb-0 ${isAI ? "gradient-font6 d-flex align-items-center gap-1" : ""
              }`}
          >
            {title}
            {isAI && <i className="bi bi-stars"></i>}
          </p>

          {!panelOpen && (
            <>
              <div className="position-relative w-55">
                <input
                  type="text"
                  className="form-control search-input ms-0"
                  placeholder="Search by Study Id, Title, Abstract or Author Name"
                  value={searchInput || ""}
                  onChange={(e) => setSearchInput?.(e.target.value)}
                  onKeyPress={onSearchKeyPress}
                />
                <button
                  className="ev-btn-lightblue-fill-round search-bar-button"
                  onClick={onSearch}
                >
                  <i className="bi bi-search"></i>
                </button>
              </div>

            </>
          )}
        </div>

        {/* CENTER: Panel Navigation (when panel is open) */}
        {panelOpen && (
          <div className="d-flex justify-content-between ev-card-4 ev-card-8 p-1 ev-radius-xsmall w-100 border-0">
            <div className="d-flex gap-2 align-items-center ps-1">
              <button
                className="ev-btn-light-fill ev-btn-reg px-4"
                disabled={!navData?.previous_id}
                onClick={onPanelPrev}
              >
                Previous
              </button>

              <div className="ev-btn-light-fill ev-btn-reg px-4">
                ID. {navData?.study_id || navData?.current_index || 0}
              </div>

              <button
                className="ev-btn-light-fill ev-btn-reg px-4"
                disabled={!navData?.next_id}
                onClick={onPanelNext}
              >
                Next
              </button>

              {mode !== "AI" && !isQC && !isQC_FT && mode !== "AI_FT" && (
                <div className="ev-btn-lightblue-fill-round ev-btn-border-square ev-btn-reg">
                  {isConflict || isConflict_FT
                    ? counts.unresolved
                    : counts.pending}
                  / {counts.total} Remaining
                </div>
              )}
            </div>

            {/* RIGHT: Actions */}
            <div className="d-flex gap-2 pe-2">
              {showSaveButton && (
                <button
                  className="ev-btn-blue-fill-round ev-btn-reg px-3 pt-0 pb-0"
                  disabled={!canSave || isSaving}
                  onClick={onSaveClick}
                >
                  {isSaving ? (
                    <div className="d-flex align-items-center">
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      <span>{isQC || isQC_FT ? "Updating" : "Saving"}</span>
                    </div>
                  ) : isQC || isQC_FT ? (
                    "Update"
                  ) : (
                    "Save"
                  )}
                </button>
              )}

              <button
                className="ev-btn-blue-outline-round ev-btn-reg ev-bold-reg px-3 py-0"
                onClick={() => setPanelOpen(false)}
              >
                Close Panel
              </button>
            </div>
          </div>
        )}

        {!panelOpen && (
          <div className="d-flex gap-2">
            {(completedOnly || (stageCompleted && qcLockMessage)) && (
              <span className="ev-alert-card-warning ev-btn-small ev-radius-large ev-bold-med d-flex align-items-center text-nowrap">
                <i className="bi bi-check-circle me-1"></i>{completedText}
              </span>
            )}
            {(isAI || mode === "AI_FT") && !completedOnly && (
              <div className="d-flex align-items-center gap-2 ms-2">

                {/* AI STATUS BADGE */}
                <span className="d-flex align-items-center">
                  <StatusBadgeSm status={aiStatus} />
                </span>

                {(aiStatus === "RUNNING" || aiStatus === "STOPPING") && (
                  <button
                    className="ev-btn-red-fill-round ev-btn-small ev-bold-med text-nowrap avatar-tooltip"
                    style={{
                      opacity: aiStatus === "STOPPING" ? 0.5 : 1,
                    }}
                    onClick={onStopAI}
                    disabled={aiStatus === "STOPPING"}
                    data-tooltip="Stop AI Screening"
                  >
                    Stop Screening
                  </button>
                )}

                {/* BUTTON GROUP */}
                <div className="btn-group custom-btn-group-outer">
                  <button
                    className="btn genbi-custom-btn-group-inner ev-btn-gradient-fill-round avatar-tooltip text-nowrap"
                    onClick={() => {
                      const ids = selectedCardIds.size > 0 ? Array.from(selectedCardIds) : null;
                      onRunAI(buildRestartFilterSummary(), ids);
                    }}
                    // Disabled if explicitly disabled, loading, OR if AI is currently running/stopping
                    disabled={
                      !!isRunDisabled ||
                      aiLockedByQC ||
                      loading ||
                      aiStatus === "RUNNING" ||
                      aiStatus === "STOPPING"
                    }
                    title={
                      aiLockedByQC
                        ? aiLockMessage
                        : isRunDisabled ||
                        aiStatus === "RUNNING" ||
                        aiStatus === "STOPPING"
                        ? "AI screening is running. Please wait until it completes."
                        : "Run"
                    }
                  >
                    <i className="bi bi-play-fill"></i> Run
                  </button>

                  <button
                    className="btn genbi-custom-btn-group-inner ev-btn-white-fill-round avatar-tooltip text-nowrap"
                    onClick={() => {
                      const ids = selectedCardIds.size > 0 ? Array.from(selectedCardIds) : null;
                      onRestartAI(buildRestartFilterSummary(), ids);
                    }}
                    disabled={
                      !!isRunDisabled ||
                      aiLockedByQC ||
                      loading ||
                      aiStatus === "RUNNING" ||
                      aiStatus === "STOPPING"
                    }
                    title={
                      aiLockedByQC
                        ? aiLockMessage
                        : isRunDisabled ||
                        aiStatus === "RUNNING" ||
                        aiStatus === "STOPPING"
                        ? "AI screening is running. Please wait until it completes."
                        : "Restart"
                    }
                  >
                    <i className="bi bi-arrow-clockwise"></i> Restart
                  </button>
                </div>
              </div>
            )}

            <button
              className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-med text-nowrap"
              onClick={onDownloadQC}
              disabled={loading || isDownloadingQC}
            >
              <i className="bi bi-download pe-1"></i> Download Results
            </button>

            {/* Add Paper — Full-Text QC only, visible once Full-Text QC is marked complete.
                Direct multi-PDF picker; status is shown like the Migrate split-pill. */}
            {isQC_FT && isQCCompleted && (
              <>
                <input
                  ref={addPaperInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    onAddPapers?.(e.target.files);
                    e.target.value = "";
                  }}
                />
                <div className="d-flex align-items-center btn-group custom-btn-group-outer">
                  {addPaperStatus === "RUNNING" && (
                    <span className="btn genbi-custom-btn-group-inner ev-btn-yellow-fill-round ev-btn-small ev-radius-large">
                      Running
                    </span>
                  )}
                  {addPaperStatus === "COMPLETED" && (
                    <span className="btn genbi-custom-btn-group-inner ev-btn-blue-fill-round ev-btn-small ev-radius-large">
                      Completed
                    </span>
                  )}
                  {addPaperStatus === "FAILED" && (
                    <span className="btn genbi-custom-btn-group-inner ev-btn-red-fill-round ev-btn-small ev-radius-large">
                      Failed
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn genbi-custom-btn-group-inner ev-btn-gradient-fill-round ev-btn-small ev-bold-med text-nowrap"
                    onClick={() => addPaperInputRef.current?.click()}
                    disabled={loading}
                    title="Select one or more PDF papers to add directly to Abstracts"
                  >
                    Add Paper
                  </button>
                </div>
              </>
            )}

            {/* Export / Import screening template — Human abstract & full-text screening.
                Both always visible: creator has full access without allocation (backend
                auto-creates allocation rows on import); reviewer imports their own papers.
                Hidden once the stage is completed (QC locked). */}
            {(mode === "HUMAN" || mode === "HUMAN_FT") && !completedOnly && (
              <>
                <button
                  className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-med text-nowrap"
                  onClick={onOpenExportModal}
                  disabled={loading || isExporting}
                >
                  <i className="bi bi-file-earmark-excel pe-1"></i> Export List
                </button>

                <button
                  className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-med text-nowrap"
                  onClick={() => importFileInputRef.current?.click()}
                  disabled={loading || isImporting}
                >
                  <i className="bi bi-upload pe-1"></i> Import Decisions
                </button>
                <input
                  ref={importFileInputRef}
                  type="file"
                  accept=".xlsx"
                  style={{ display: "none" }}
                  onChange={handleImportFileChange}
                />
              </>
            )}

            {/* SET toggle — only for owner in DUAL_HUMAN_HUMAN human screening */}
            {isOwner && framework === "DUAL_HUMAN_HUMAN" && (mode === "HUMAN" || mode === "HUMAN_FT") && (
              <div className="d-flex align-items-center gap-2">
                <span className="ev-xsmall-p-dark text-nowrap mb-0">Screen as</span>
                <div className="ev-view-toggle-shell">
                  <button
                    type="button"
                    className={`ev-view-toggle-btn ${reviewerSet === "SET_1" ? "active" : ""}`}
                    onClick={() => onReviewerSetChange?.("SET_1")}
                  >
                    Set 1
                  </button>
                  <button
                    type="button"
                    className={`ev-view-toggle-btn ${reviewerSet === "SET_2" ? "active" : ""}`}
                    onClick={() => onReviewerSetChange?.("SET_2")}
                  >
                    Set 2
                  </button>
                </div>
              </div>
            )}

            {/* Refresh */}
            <button
              className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-med text-nowrap"
              onClick={onRefresh}
            >
              <i className="bi bi-arrow-repeat"></i> Refresh
            </button>
          </div>
        )}
      </div>

      {/* ================= BODY ================= */}
      {!panelOpen && (
        <div className="flex-grow-1 overflow-hidden">
          <div className="row gx-3 h-100">
            {/* LEFT FILTERS */}
            <div className={filtersCollapsed ? "col-auto" : "ev-filter-panel-fixed"}>
              <div className={filtersCollapsed ? "" : "vh-65 scrollable-element pe-1"}>

                {/* ── Filtering header (collapse toggle) ── */}
                <div
                  className="ev-card-2 ev-radius-xsmall d-flex align-items-center justify-content-between p-1 mb-2 cursor-pointer gap-2 position-relative"
                  onClick={() => setFiltersCollapsed(prev => !prev)}
                >
                  {!filtersCollapsed && (
                    <>
                      <p className="ev-med-p-dark-blue ev-bold-med mb-0 position-absolute start-50 translate-middle-x">Filtering</p>
                      <div></div>
                    </>
                  )}
                  <i className={`bi ${filtersCollapsed ? "bi-funnel px-1" : "bi-chevron-left"} ev-med-p-dark-blue`}></i>
                  {filtersCollapsed && activeFilterCount > 0 && (
                    <span className="ev-message-card3 ev-bold-med ev-radius-large"
                      style={{ fontSize: "var(--px-10)", padding: "0 6px" }}>
                      {activeFilterCount}
                    </span>
                  )}
                </div>

                {!filtersCollapsed && (
                  <div className="d-flex flex-column gap-2">

                    {/* ══════════════════════════════
                        SECTION 1 — Basic Filters
                    ══════════════════════════════ */}
                    <div className="ev-card-8 ev-radius-small p-2">

                      {/* Section header */}
                      <div
                        className="d-flex align-items-center gap-2 cursor-pointer"
                        onClick={() => setSection1Open(p => !p)}
                      >
                        <span className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                          style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}>
                          1
                        </span>
                        <p className="ev-small-p-dark ev-bold-med mb-0 flex-grow-1">Basic Filters</p>
                        <i className={`bi ${section1Open ? "bi-chevron-up" : "bi-chevron-down"} ev-xsmall-p-dark`}></i>
                      </div>

                      {section1Open && (
                        <div className="mt-2">

                          {/* Publication Year */}
                          <p className="ev-xsmall-p-dark ev-bold-med mb-1">Publication Year</p>
                          <YearRangePicker
                            from={dateFrom}
                            to={dateTo}
                            onFromChange={setDateFrom}
                            onToChange={setDateTo}
                          />

                          {/* Decision */}
                          <p className="ev-xsmall-p-dark ev-bold-med mb-1">Decision</p>

                          {/* Main toggle: All / Included / Excluded */}
                          <div className="d-flex gap-2 mb-2">
                            <button
                              className={`ev-btn-light-fill ev-btn-reg flex-grow-1 ${!decisionFilter && !pendingDecisionFilter ? "active" : ""}`}
                              onClick={() => { setDecisionFilter(null); setPendingDecisionFilter(null); setReviewerFilter?.(null); setPendingReviewerFilter(null); setReasonFilter?.(null); setReasonChosen(false); setReasonDropdownOpen(false); }}
                            >
                              All
                            </button>
                            <button
                              className={`ev-btn-light-fill ev-btn-reg flex-grow-1 ${(decisionFilter === "INCLUDED" || pendingDecisionFilter === "INCLUDED") ? "active" : ""}`}
                              onClick={() => {
                                setReasonFilter?.(null); setReasonChosen(false);
                                setPendingReviewerFilter(null);
                                setReasonDropdownOpen(false);
                                if ((isAI || mode === "AI_FT") && aiReviewerCount > 1) {
                                  setPendingDecisionFilter("INCLUDED");
                                  setReviewerFilter?.(null);
                                } else {
                                  setPendingDecisionFilter(null);
                                  setDecisionFilter("INCLUDED");
                                }
                              }}
                            >
                              Included
                            </button>
                            <button
                              className={`ev-btn-light-fill ev-btn-reg flex-grow-1 ${(decisionFilter === "EXCLUDED" || pendingDecisionFilter === "EXCLUDED") ? "active" : ""}`}
                              onClick={() => {
                                setReasonFilter?.(null); setReasonChosen(false);
                                setPendingReviewerFilter(null);
                                setReasonDropdownOpen(false);
                                if ((isAI || mode === "AI_FT") && aiReviewerCount > 1) {
                                  setPendingDecisionFilter("EXCLUDED");
                                  setReviewerFilter?.(null);
                                } else {
                                  setPendingDecisionFilter(null);
                                  setDecisionFilter("EXCLUDED");
                                }
                              }}
                            >
                              Excluded
                            </button>
                          </div>

                          {/* Conditional flat buttons — logic untouched */}
                          <div className="d-flex gap-2 flex-wrap">
                            {/* Bibliographic — papers added via Full-Text QC → Add Paper (every list, hidden until any exist) */}
                            {bibliographicIds.size > 0 && (
                              <button
                                className={`ev-btn-light-fill ev-btn-reg ${decisionFilter === "BIBLIOGRAPHIC" ? "active" : ""}`}
                                onClick={() => {
                                  setPendingDecisionFilter(null);
                                  setReviewerFilter?.(null);
                                  setPendingReviewerFilter(null);
                                  setReasonFilter?.(null); setReasonChosen(false);
                                  setReasonDropdownOpen(false);
                                  setDecisionFilter("BIBLIOGRAPHIC");
                                }}
                              >
                                <i className="bi bi-journal-text pe-1" />
                                Bibliographic
                              </button>
                            )}
                            {(isAI || mode === "AI_FT") && (
                              <button
                                className={`ev-btn-light-fill ev-btn-reg ${decisionFilter === "ACTION_NEEDED" ? "active" : ""}`}
                                onClick={() => setDecisionFilter("ACTION_NEEDED")}
                              >
                                Review Needed
                              </button>
                            )}
                            {(mode === "HUMAN" || mode === "HUMAN_FT") && (
                              <button
                                className={`ev-btn-light-fill ev-btn-reg ${decisionFilter === "PENDING" ? "active" : ""}`}
                                onClick={() => { setPendingDecisionFilter(null); setDecisionFilter("PENDING"); }}
                              >
                                Remaining
                              </button>
                            )}
                            {(isConflict || isConflict_FT) && (
                              <button
                                className={`ev-btn-light-fill ev-btn-reg ${decisionFilter === "UNRESOLVED" ? "active" : ""}`}
                                onClick={() => setDecisionFilter("UNRESOLVED")}
                              >
                                Unresolved
                              </button>
                            )}
                          </div>

                          {/* Agent filter — AI mode only, when Include or Exclude is selected */}
                          {(isAI || mode === "AI_FT") &&
                            (decisionFilter === "INCLUDED" || decisionFilter === "EXCLUDED" || pendingDecisionFilter === "INCLUDED" || pendingDecisionFilter === "EXCLUDED") &&
                            aiReviewerCount > 1 && (
                              <div className="mt-2">
                                <p className="ev-xsmall-p-dark ev-bold-med mb-1">Agent</p>

                                {/* Trigger button — same style as other filter buttons */}
                                <button
                                  className="ev-btn-light-fill ev-btn-reg w-100 d-flex align-items-center justify-content-between"
                                  onClick={() => setAgentScopeOpen(o => !o)}
                                >
                                  <span>
                                    {(() => {
                                      const active = reviewerFilter || pendingReviewerFilter;
                                      if (!active) return "Select scope...";
                                      if (active === "ANY") return "Any Agent";
                                      if (active === "ALL") return "All Agents";
                                      return aiAgents.find(a => String(a.agent_id) === active)?.label ?? "Agent";
                                    })()}
                                  </span>
                                  <i className={`bi bi-chevron-${agentScopeOpen ? "up" : "down"} ms-1`}></i>
                                </button>

                                {/* Options — inline, pushes panel content down, no floating overlay */}
                                {agentScopeOpen && (
                                  <div className="mt-1 d-flex flex-column gap-1">
                                    {[
                                      { value: "ANY", label: "Any Agent" },
                                      { value: "ALL", label: "All Agents" },
                                      ...aiAgents.map((a, i) => ({ value: String(a.agent_id ?? i), label: a.label })),
                                    ].map(opt => (
                                      <button
                                        key={opt.value}
                                        className={`ev-btn-light-fill ev-btn-reg w-100 text-start ${(reviewerFilter === opt.value || pendingReviewerFilter === opt.value) ? "active" : ""}`}
                                        onClick={() => {
                                          if (pendingDecisionFilter) {
                                            const expectedType = pendingDecisionFilter === "INCLUDED" ? "include" : "exclude";
                                            const hasCriteria = criteriaList.filter(c => c.type === expectedType).length > 0;
                                            if (hasCriteria) {
                                              // Criteria exist — hold everything until reason is also selected
                                              setPendingReviewerFilter(opt.value);
                                              setAgentScopeOpen(false);
                                              setReasonDropdownOpen(true); // reason is the next step
                                              return;
                                            }
                                            // No criteria — commit immediately
                                            setDecisionFilter(pendingDecisionFilter);
                                            setPendingDecisionFilter(null);
                                          }
                                          setTimeout(() => setReviewerFilter?.(opt.value), 0);
                                          setAgentScopeOpen(false);
                                        }}
                                      >
                                        {opt.label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                          {/* Reason filter */}
                          {(() => {
                            const isMultiAI = (isAI || mode === "AI_FT") && aiReviewerCount > 1;
                            const decisionActive = decisionFilter === "INCLUDED" || decisionFilter === "EXCLUDED";
                            const pendingActive = pendingDecisionFilter === "INCLUDED" || pendingDecisionFilter === "EXCLUDED";

                            // Nothing selected
                            if (!decisionActive && !pendingActive) return null;

                            // Multi-AI: don't show reason until agent is also chosen
                            if (isMultiAI && !pendingReviewerFilter && !reviewerFilter) return null;

                            const activeDecision = decisionFilter || pendingDecisionFilter;
                            const expectedType = activeDecision === "INCLUDED" ? "include" : "exclude";
                            const filtered = criteriaList.filter(c => c.type === expectedType);
                            if (!filtered.length) return null;

                            // Multi-AI: decision + agent are held until a reason is picked.
                            const awaitingReason = !!(pendingDecisionFilter && pendingReviewerFilter);

                            return (
                              <div className="mt-2">
                                <p className="ev-xsmall-p-dark ev-bold-med mb-1">Reason</p>
                                {awaitingReason && (
                                  <p className="ev-xxsmall-p-blue mb-1">
                                    <i className="bi bi-info-circle me-1" />
                                    Select a reason to apply this filter
                                  </p>
                                )}
                                <button
                                  className="ev-btn-light-fill ev-btn-reg w-100 d-flex align-items-center justify-content-between"
                                  onClick={() => setReasonDropdownOpen(o => !o)}
                                >
                                  <span>
                                    {reasonFilter
                                      ? (filtered.find(c => c.id === reasonFilter)?.label ?? "Reason")
                                      : (reasonChosen ? "All Reasons" : "Select Reason")}
                                  </span>
                                  <i className={`bi bi-chevron-${reasonDropdownOpen ? "up" : "down"} ms-1`}></i>
                                </button>
                                {reasonDropdownOpen && (
                                  <div className="mt-1 d-flex flex-column gap-1">
                                    {[{ value: null, label: "All Reasons" }, ...filtered.map(c => ({ value: c.id, label: c.label }))].map(opt => (
                                      <button
                                        key={opt.value ?? "all"}
                                        className={`ev-btn-light-fill ev-btn-reg w-100 text-start ${reasonFilter === opt.value && (opt.value !== null || reasonChosen) ? "active" : ""}`}
                                        onClick={() => {
                                          setReasonChosen(true);
                                          if (pendingDecisionFilter && pendingReviewerFilter) {
                                            // Multi-AI pending: commit decision + agent + reason all at once → single API call
                                            setDecisionFilter(pendingDecisionFilter);
                                            setPendingDecisionFilter(null);
                                            setReviewerFilter?.(pendingReviewerFilter);
                                            setPendingReviewerFilter(null);
                                          }
                                          setReasonFilter?.(opt.value);
                                          setReasonDropdownOpen(false);
                                        }}
                                      >
                                        {opt.label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Confidence Threshold — only for AI-involved frameworks in conflict/QC modes */}
                          {hasAI && (isConflict || isConflict_FT || isQC || isQC_FT) && (
                            <div className="mt-2">
                              <p className="ev-xsmall-p-dark ev-bold-med mb-1">Confidence Threshold</p>
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={localConfStart}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") {
                                      setLocalConfStart("");
                                    } else {
                                      const num = parseFloat(val);
                                      if (!isNaN(num) && num >= 0 && num <= 100) {
                                        setLocalConfStart(val);
                                      }
                                    }
                                  }}
                                  className="formField4 ev-btn-small"
                                  placeholder="0"
                                />
                                <span className="ev-xsmall-p-dark ev-bold-med">—</span>
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={localConfEnd}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") {
                                      setLocalConfEnd("");
                                    } else {
                                      const num = parseFloat(val);
                                      if (!isNaN(num) && num >= 0 && num <= 100) {
                                        setLocalConfEnd(val);
                                      }
                                    }
                                  }}
                                  className="formField4 ev-btn-small"
                                  placeholder="100"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ══════════════════════════════
                        SECTION 2 — Tags
                    ══════════════════════════════ */}
                    <div className="ev-card-8 ev-radius-small p-2">

                      {/* Section header */}
                      <div
                        className="d-flex align-items-center gap-2 cursor-pointer"
                        onClick={() => setSection2Open(p => !p)}
                      >
                        <span className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                          style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}>
                          2
                        </span>
                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                          <p className="ev-small-p-dark ev-bold-med mb-0">Tags</p>
                          {selectedTags?.length > 0 && (
                            <span className="ev-message-card3 ev-btn-xsmall">
                              {selectedTags.length} selected
                            </span>
                          )}
                        </div>
                        <i className={`bi ${section2Open ? "bi-chevron-up" : "bi-chevron-down"} ev-xsmall-p-dark`}></i>
                      </div>

                      {section2Open && (
                        <div className="mt-2">

                          {/* Tag selection panel */}
                          <TagFilterPanel
                            tagOptions={tagOptions}
                            selectedTagIds={selectedTags}
                            onToggleTag={onToggleTag}
                            onClearAll={() => selectedTags.forEach((id) => onToggleTag(id))}
                            tagsHeight={isQC || isQC_FT ? "vh-15" : "vh-20"}
                          />
                        </div>
                      )}
                    </div>

                    {/* ══════════════════════════════
                        SECTION 3 — Advanced Filtering
                    ══════════════════════════════ */}
                    <div className="ev-card-8 ev-radius-small p-2">

                      {/* Section header — click expands/collapses; the switch turns the filter on/off */}
                      <div
                        className="d-flex align-items-center gap-2 cursor-pointer"
                        onClick={() => setSection3Open(p => !p)}
                      >
                        <span className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                          style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}>
                          3
                        </span>
                        <p className="ev-small-p-dark ev-bold-med mb-0 flex-grow-1">Advanced Filtering</p>
                        <div
                          className="form-check form-switch fs-6 mb-0 ms-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            const newVal = !advancedEnabled;
                            setAdvancedEnabled(newVal);
                            setSection3Open(newVal);
                            if (!newVal) setAdvOpenDropdown(null);
                          }}
                        >
                          <input
                            className="form-check-input cursor-pointer"
                            type="checkbox"
                            checked={Boolean(advancedEnabled)}
                            aria-label="Enable Advanced Filtering"
                            onChange={() => {}}
                          />
                        </div>
                      </div>

                      {!section3Open && advancedEnabled && advConditions.some(c => c.category || c.value) && (
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <span className="ev-small-p-dark">
                            {advConditions.filter(c => c.category || c.value).length} conditions applied
                          </span>
                        </div>
                      )}

                      {section3Open && (
                        <div className="mt-2">

                          {/* Unified filter rules */}
                          <div>
                            {advConditions.map((cond, idx) => (
                              <div key={cond.id}>
                                {idx > 0 && (
                                  <div className="d-flex justify-content-center mb-2">
                                    <InlineSelect
                                      id={`${cond.id}:connector`}
                                      openId={advOpenDropdown}
                                      setOpenId={setAdvOpenDropdown}
                                      width={90}
                                      value={cond.connector || "AND"}
                                      placeholder="AND"
                                      options={["AND", "OR"].map((v) => ({
                                        value: v,
                                        label: v,
                                        onSelect: () => updateAdvCondition(cond.id, "connector", v),
                                      }))}
                                    />
                                  </div>
                                )}
                                <div className="d-flex align-items-start gap-1 mb-2">
                                  <InlineSelect
                                    id={`${cond.id}:field`}
                                    openId={advOpenDropdown}
                                    setOpenId={setAdvOpenDropdown}
                                    className="flex-grow-1"
                                    value={cond.category}
                                    placeholder="Field"
                                    options={ADV_FIELD_OPTIONS.map((o) => ({
                                      ...o,
                                      onSelect: () => updateAdvCondition(cond.id, "category", o.value),
                                    }))}
                                  />
                                  <InlineSelect
                                    id={`${cond.id}:operator`}
                                    openId={advOpenDropdown}
                                    setOpenId={setAdvOpenDropdown}
                                    className="flex-grow-1"
                                    value={cond.filterOp || "contains"}
                                    placeholder="Contains"
                                    options={ADV_OPERATOR_OPTIONS.map((o) => ({
                                      ...o,
                                      onSelect: () => updateAdvCondition(cond.id, "filterOp", o.value),
                                    }))}
                                  />
                                  {!["is_empty", "is_not_empty", "is_null", "is_not_null"].includes(cond.filterOp) && (
                                    <input
                                      type="text"
                                      className="formField4 ev-btn-small ev-radius-xsmall flex-grow-1"
                                      placeholder="Value"
                                      value={cond.value}
                                      onChange={(e) => updateAdvCondition(cond.id, "value", e.target.value)}
                                    />
                                  )}
                                  {advConditions.length > 1 && (
                                    <button
                                      className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall border-0"
                                      onClick={() => removeAdvCondition(cond.id)}
                                    >
                                      <i className="bi bi-x-lg"></i>
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                            <button
                              className="ev-btn-text-only-fill ev-btn-small p-0"
                              onClick={addAdvCondition}
                            >
                              <i className="bi bi-plus-lg me-1"></i> Add rule
                            </button>
                          </div>

                          {/* Clear + Apply inside Section 3 */}
                          <div className="d-flex gap-1 mt-3">
                            <button
                              className="ev-btn-blue-outline-round ev-btn-reg ev-radius-small w-50"
                              onClick={() => setAdvConditions([{ id: 1, category: "", filterOp: "contains", connector: "AND", value: "" }])}
                            >
                              Clear
                            </button>
                            <button
                              className="ev-btn-blue-fill-round ev-btn-reg ev-radius-xsmall w-50"
                              onClick={onApplyAdvancedFilters}
                            >
                              Apply Filters
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                )}

              </div>
            </div>

            {/* RIGHT LIST */}
            <div className="col h-100 d-flex flex-column">

              {/* ── Metric card — inline "20 Results" format, compact height ── */}
              <div className="ev-card-8 d-flex align-items-center ev-radius-small mb-3 overflow-hidden px-2">
                {metrics.map((m, index) => (
                  <MetricCard
                    key={index}
                    value={m.value}
                    label={m.label}
                    valueClassName={m.valueClassName}
                    showDivider={index > 0}
                  />
                ))}
              </div>

              {/* ── Action buttons below metric card ── */}
              {(advancedEnabled && (mode === "HUMAN" || mode === "CONFLICT" || mode === "HUMAN_FT" || mode === "CONFLICT_FT")) || (isQC || isQC_FT) || (mode === "AI" || mode === "AI_FT") ? (
                <div className="position-relative d-flex gap-3 mb-2 flex-shrink-0 align-items-center" ref={bulkDropdownRef}>
                  {/* Select All for AI mode */}
                  {(mode === "AI" || mode === "AI_FT") && !completedOnly && (
                    <>
                      <label className="d-flex align-items-center gap-1 ev-small-p-dark ev-bold-med cursor-pointer mb-0">
                        <input
                          type="checkbox"
                          checked={isAllPageSelected}
                          onChange={toggleSelectAll}
                        />
                        {isAllPageSelected ? "Unselect All" : "Select All"}
                      </label>
                      {selectedCardIds.size > 0 && (
                        <span className="ev-xsmall-p-dark ev-bold-reg d-flex align-items-center">
                          {selectedCardIds.size} selected
                        </span>
                      )}
                    </>
                  )}
                  {/* Bulk actions */}
                  {(mode === "HUMAN" || mode === "HUMAN_FT") && !readOnly && !completedOnly && (
                    <>
                      <label className="d-flex align-items-center gap-1 ev-small-p-dark ev-bold-med cursor-pointer mb-0">
                        <input
                          type="checkbox"
                          checked={isAllPageSelected}
                          onChange={toggleSelectAll}
                        />
                        Select All
                      </label>
                      {selectedCardIds.size > 0 && (
                        <span className="ev-xsmall-p-dark ev-bold-reg d-flex align-items-center">
                          {selectedCardIds.size} selected
                        </span>
                      )}
                      <button
                        className="ev-btn-green-fill-round ev-btn-reg ev-bold-med"
                        onClick={() => {
                          const isAbstractMode = mode === "HUMAN" || mode === "CONFLICT";
                          if (isAbstractMode) {
                            // Abstract screening: skip criteria dropdown, fire directly
                            const ids = selectedCardIds.size > 0 ? Array.from(selectedCardIds) : null;
                            onBulkDecision?.("INCLUDED", [], ids);
                            setSelectedCardIds(new Set());
                          } else {
                            setBulkDropdownOpen(bulkDropdownOpen === "INCLUDED" ? null : "INCLUDED");
                            setSelectedBulkCriteriaIds([]);
                          }
                        }}
                        disabled={isBulkLoading || loading || selectedCardIds.size === 0}
                      >
                        <i className="bi bi-check-circle me-1"></i>Bulk Include
                      </button>

                      <button
                        className="ev-btn-red-fill-round ev-btn-reg ev-bold-med"
                        onClick={() => {
                          setBulkDropdownOpen(bulkDropdownOpen === "EXCLUDED" ? null : "EXCLUDED");
                          setSelectedBulkCriteriaIds([]);
                        }}
                        disabled={isBulkLoading || loading || selectedCardIds.size === 0}
                      >
                        <i className="bi bi-x-circle me-1"></i>Bulk Exclude
                      </button>
                    </>
                  )}

                  {/* Mark as Complete */}
                  {(isQC || isQC_FT) && (
                    <button
                      type="button"
                      className="ev-message-card3-green ev-bold-med border-0 ev-btn-reg"
                      onClick={onMarkComplete}
                      disabled={loading || isQCCompleted}
                    >
                      <i
                        className={`bi ${
                          isQCCompleted
                            ? "bi-check-circle-fill"
                            : "bi-check-circle"
                        } me-1`}
                      ></i>

                      {loading
                        ? "Processing..."
                        : isQCCompleted
                          ? "Completed"
                          : "Mark as Complete"}
                    </button>
                  )}

                  {/* Criteria dropdown */}
                  {bulkDropdownOpen && (
                    <div
                      className="ev-card-8 ev-radius-xsmall position-absolute top-100 start-0 mt-1 p-2 shadow z-3"
                    >
                      <p className="ev-small-p-dark ev-bold-med mb-2">
                        Select criteria for Bulk {bulkDropdownOpen === "INCLUDED" ? "Include" : "Exclude"}
                      </p>

                      {(() => {
                        const expectedType = bulkDropdownOpen === "INCLUDED" ? "include" : "exclude";
                        const filtered = criteriaList.filter((c) => c.type === expectedType);
                        if (filtered.length === 0) {
                          return <p className="ev-reg-p-dark text-muted mb-2">No {expectedType === "include" ? "inclusion" : "exclusion"} criteria configured.</p>;
                        }
                        return (
                          <div className="d-flex flex-column gap-1 mb-2 scrollable-element mt-2 vh-20">
                            {filtered.map((c) => {
                              const isChecked = selectedBulkCriteriaIds.includes(c.id);
                              return (
                                <label
                                  key={c.id}
                                  className={`d-flex align-items-center gap-2 px-2 py-1 ev-radius-xsmall cursor-pointer ev-reg-p-dark ${
                                    c.type === "include" ? "ev-message-card5" : "ev-message-card5"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleBulkCriteria(c.id)}
                                  />
                                  {c.label}
                                </label>
                              );
                            })}
                          </div>
                        );
                      })()}

                      <div className="d-flex gap-1">
                        <button
                          className="ev-btn-light-fill ev-btn-small w-50"
                          onClick={() => { setBulkDropdownOpen(null); setSelectedBulkCriteriaIds([]); }}
                        >
                          Cancel
                        </button>
                        <button
                          className={`ev-btn-small w-50 ev-bold-med ${bulkDropdownOpen === "INCLUDED" ? "ev-btn-green-fill-round" : "ev-btn-red-fill-round"}`}
                          onClick={handleBulkConfirm}
                          disabled={!selectedBulkCriteriaIds.length || isBulkLoading}
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : <div className="mb-2"></div>}

              <div className="scrollable-element vh-50 flex-grow-1 pe-1">
                {Array.isArray(listData) &&
                  listData.map((item) => {
                    let rawDecision = null;
                    let aiDecisions = null;
                    let isDuplicateCard = false;

                    if (isQC || isQC_FT) {
                      rawDecision = item.final_decision;
                      isDuplicateCard = item.duplicate_boolean === true;
                    } else if (mode === "AI" || mode === "AI_FT") {
                      aiDecisions = item.ai_decisions || [];
                      rawDecision = aiDecisions[0]?.decision;
                      isDuplicateCard =
                        aiDecisions.length > 0 &&
                        aiDecisions.every((d) =>
                          (d.summary || "").toLowerCase().includes("duplicate entry")
                        );
                    } else if (mode === "CONFLICT" || mode === "CONFLICT_FT") {
                      rawDecision = item.resolution_decision || null;
                    } else {
                      // HUMAN / HUMAN_FT
                      rawDecision = item.human_decision;
                      isDuplicateCard = (item.human_comments || "")
                        .toLowerCase()
                        .includes("duplicate entry");
                    }

                    const status = rawDecision
                      ? rawDecision.charAt(0).toUpperCase() +
                      rawDecision.slice(1).toLowerCase()
                      : null;

                    const conflictBadge =
                      (mode === "CONFLICT" || mode === "CONFLICT_FT") &&
                        item.conflict_status
                        ? String(item.conflict_status).toUpperCase()
                        : null;

                    // Inside AbstractCard component, when determining idForPanel:
                    let idForPanel;

                    if (mode === "CONFLICT_FT" || mode === "QC_FT") {
                      // Use screening_id for conflict/QC
                      idForPanel = item.screening_id || item.id;
                    } else if (mode === "HUMAN_FT" || mode === "AI_FT") {
                      // Use full_text_screening_id for human/AI
                      idForPanel = item.full_text_screening_id;
                    } else {
                      // Abstract modes use study_id
                      idForPanel = item.study_id || item.id;
                    }

                    return (
                      <AbstractCard
                        key={`${mode}-${isFullTextMode ? item.screening_id || item.id : item.study_id || item.id}`}
                        mode={mode}
                        data={item}
                        status={status}
                        aiDecisions={aiDecisions}
                        conflictBadge={conflictBadge}
                        isDuplicateCard={isDuplicateCard}
                        isFullTextMode={isFullTextMode}
                        showCheckbox={showBulkCheckboxes}
                        isChecked={selectedCardIds.has(idForPanel)}
                        onCheckToggle={() => toggleCardSelection(idForPanel)}
                        onOpenPanel={() => {
                          setActiveCardId(idForPanel);
                          onOpenPanel(idForPanel);
                        }}
                        onPreviewPdf={onPreviewPdf}
                        bibliographicIds={bibliographicIds}
                      />
                    );
                  })}
              </div>

              {/* ================= PAGE NAVIGATION ================= */}
              <div className="d-flex justify-content-between align-items-center mt-2 pb-1">
                <span className="ev-xsmall-p-dark">
                  Showing {listData.length} of {pagination.total} Results
                </span>

                <div className="d-flex align-items-center gap-2">
                  <button
                    className="ev-btn-light-fill ev-btn-xsmall"
                    disabled={page === 1}
                    onClick={onPagePrev}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  <button
                    className="ev-btn-light-fill ev-btn-xsmall"
                    disabled
                  >
                    Page {page} of {pagination.total_pages || 1}
                  </button>

                  <button
                    className="ev-btn-light-fill ev-btn-xsmall"
                    disabled={page >= pagination.total_pages}
                    onClick={onPageNext}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </div>

                <div className="text-end">
                  <PaginationControls
                    initialSize={pageSize}
                    onSizeChange={onPageSizeChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {panelOpen && (
        <div className="flex-grow-1 overflow-auto">{panelContent}</div>
      )}
    </div>
  );
};

const AbstractCard = ({
  mode,
  data,
  status,
  aiDecisions,
  conflictBadge,
  isDuplicateCard,
  isFullTextMode,
  showCheckbox = false,
  isChecked = false,
  onCheckToggle,
  onOpenPanel,
  onPreviewPdf,
  bibliographicIds = EMPTY_ID_SET,
}) => {
  const [showAbstract, setShowAbstract] = useState(false);
  // Badge hidden in AI screening for now (header row layout) — filter still works there.
  const isAiMode = mode === "AI" || mode === "AI_FT";
  const isBibliographic =
    !isAiMode &&
    (!!data?.manually_added || bibliographicIds.has(String(data?.study_id ?? "")));
  const [isHovered, setIsHovered] = useState(false);
  const [summaryHoverInfo, setSummaryHoverInfo] = useState(null);
  const closeTimeoutRef = useRef(null);

  const handleToggle = () => {
    if (isFullTextMode) {
      onPreviewPdf?.(data?.pdf_s3_path, data?.title, data?.id);
      return;
    }
    setShowAbstract((v) => !v);
  };

  const handleBadgeEnter = (e) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    const summary = data?.action_needed_reason;
    if (!summary) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipWidth = 380;
    const desiredHeight = 320;
    const margin = 8;

    // Horizontal: right-align to badge, clamp within viewport
    let left = rect.right - tooltipWidth;
    if (left < margin) left = margin;
    if (left + tooltipWidth > window.innerWidth - margin) {
      left = window.innerWidth - tooltipWidth - margin;
    }

    const spaceAbove = rect.top - margin;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const showBelow = spaceAbove < desiredHeight && spaceBelow > spaceAbove;
    const maxHeight = Math.min(desiredHeight, showBelow ? spaceBelow : spaceAbove);

    setSummaryHoverInfo({
      left,
      top: showBelow ? rect.bottom + margin : null,
      bottom: showBelow ? null : window.innerHeight - rect.top + margin,
      maxHeight,
      summary,
    });
  };

  const handleBadgeLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setSummaryHoverInfo(null);
    }, 150);
  };

  const handleTooltipEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleTooltipLeave = () => {
    setSummaryHoverInfo(null);
  };

  const isAIMode = mode === "AI" || mode === "AI_FT";
  const validAIDecisions =
    isAIMode && Array.isArray(aiDecisions)
      ? aiDecisions.filter((d) => d?.decision)
      : [];

  return (
    <div
      className={`ev-card-8 w-100 ev-radius-small mb-1${isHovered ? " active" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="d-flex justify-content-between align-items-start">
        <div className="d-flex gap-2 align-items-center">
          {showCheckbox && (
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => {
                e.stopPropagation();
                onCheckToggle?.();
              }}
              className="cursor-pointer"
            />
          )}
          <span className="ev-message-card5 ev-radius-xsmall ev-btn-small">
            ID. {data.study_id || data.id}
          </span>

          <span className="ev-message-card3 ev-radius-xsmall ev-btn-small">
            Year {data.publication_year || data.year}
          </span>

          {/* Bibliographic — paper added via Full-Text QC → Add Paper.
              Same size as the ID / Year chips so the header row stays aligned. */}
          {isBibliographic && (
            <span
              className="ev-message-card3 ev-radius-xsmall ev-btn-small text-nowrap avatar-tooltip"
              data-tooltip={
                data?.added_at
                  ? `Bibliographic paper · added on ${new Date(data.added_at).toLocaleDateString()}`
                  : "Bibliographic paper (added via Add Paper)"
              }
            >
              <i className="bi bi-journal-text pe-1" />
              Bibliographic
            </span>
          )}
        </div>

        <div className="d-flex gap-2 align-items-center">
          {(mode === "CONFLICT" || mode === "CONFLICT_FT") && conflictBadge && (
            <span
              className={`category-badge ev-radius-reg border-0 mb-0 ${conflictBadge === "RESOLVED"
                  ? "category-badge-1"
                  : "ev-message-card5"
                }`}
            >
              {conflictBadge === "RESOLVED" ? "Resolved" : "Unresolved"}
            </span>
          )}

          {/* Duplicate badge — shown for HUMAN and AI modes */}
          {isDuplicateCard && (
            <span className="ev-alert-card-warn p-1 ev-radius-xsmall px-3 ev-fs-11">
              Duplicate
            </span>
          )}

          {/* AI mode: duplicate case — single excluded badge only */}
          {isAIMode && isDuplicateCard && (
            <span className="ev-decision-card excluded">
              <span className="ev-decision-icon excluded">
                <i className="bi bi-x-lg" />
              </span>
              Exclude
            </span>
          )}

          {/* AI mode: normal case — all agent decisions */}
          {isAIMode && !isDuplicateCard &&
            validAIDecisions.length > 0 &&
            validAIDecisions.map((aiDecision, idx) => {
              const decision = aiDecision.decision;
              const normalizedDecision =
                decision?.charAt(0).toUpperCase() +
                decision?.slice(1).toLowerCase();
              const isIncluded = normalizedDecision === "Included";
              const sizeClass = validAIDecisions.length === 1 ? "" : "ev-btn-small";

              return (
                <span
                  key={idx}
                  className={`ev-decision-card ${sizeClass} px-1 ${isIncluded ? "included" : "excluded"}`}
                >
                  <span
                    className={`ev-decision-icon  ${isIncluded ? "included" : "excluded"}`}
                  >
                    <i className={`bi ${isIncluded ? "bi-check-lg" : "bi-x-lg"}`} />

                  </span>

                  <i className="me-3 avatar-tooltip" data-tooltip={aiDecision.model}>
                    {aiDecision.model?.slice(0, 6)} : {isIncluded ? "Include" : "Exclude"}
                  </i>
                </span>
              );
            })}

          {/* Non-AI mode: show single status */}
          {!isAIMode && status && (
            <span
              className={`ev-decision-card ${status === "Included" ? "included" : "excluded"
                }`}
            >
              <span
                className={`ev-decision-icon ${status === "Included" ? "included" : "excluded"
                  }`}
              >
                <i className={`bi ${status === "Included" ? "bi-check-lg" : "bi-x-lg"}`} />

              </span>
              {status === "Included" ? "Include" : "Exclude"}
            </span>
          )}
        </div>
      </div>

      <div className="mt-1">
        <p className="ev-bold-med ev-small-p-dark mb-0">{data.title}</p>
        <p className="ev-xsmall-p-dark mb-0 ev-bold-small">
          <i>Authors. {data.author_names || data.authors}</i>
        </p>
        {data.performed_by_name && (mode === "HUMAN" || mode === "HUMAN_FT") && (
          <p className="ev-xsmall-p-dark mb-0">
            Screened by {data.performed_by_name}
          </p>
        )}
      </div>

      <div className="d-flex justify-content-between mt-1">
        <div
          className="d-flex align-items-center gap-1 ev-small-p-blue ev-bold-med cursor-pointer"
          onClick={handleToggle}
        >
          {!isFullTextMode && (
            <>
              <span>Abstract</span>
              <i
                className={`bi ${showAbstract ? "bi-chevron-up" : "bi-chevron-down"
                  } ev-fs-12`}
              />
            </>
          )}
        </div>

        <div className="d-flex justify-content-end">
          {isAIMode && data.action_needed && (
            <span
              className="ev-alert-card-warning ev-radius-xsmall ev-btn-small px-2 d-flex align-items-center gap-1 me-2"
              onMouseEnter={handleBadgeEnter}
              onMouseLeave={handleBadgeLeave}
            >
              <i className="bi bi-exclamation-triangle-fill"></i>{" "}
              {/refused to process this paper/i.test(data.action_needed_reason || "")
                ? "AI Refused – Manual Review Needed"
                : "Configuration Review Needed"}
            </span>
          )}
          <button
            className="ev-btn-white-fill-round ev-btn-small text-nowrap border border-primary"
            onClick={onOpenPanel}
          >
            {mode === "AI" && "AI Response Panel"}
            {mode === "HUMAN" && "Open Review Panel"}
            {mode === "CONFLICT" && "Conflict Panel"}
            {mode === "QC" && "Open QC Panel"}
            {mode === "HUMAN_FT" && "Open Review Panel"}
            {mode === "AI_FT" && "AI Response Panel"}
            {mode === "CONFLICT_FT" && "Conflict Panel"}
            {mode === "QC_FT" && "Open QC Panel"}
            <i className="bi bi-box-arrow-up-right ps-2"></i>
          </button>
        </div>
      </div>

      {!isFullTextMode && showAbstract && (
        <div className="mt-2 ev-card-8 p-2 ev-radius-xsmall">
          <p className="ev-xsmall-p-dark mb-0">
            {(!(data.abstract_text || data.abstract) || (data.abstract_text || data.abstract) === "nan")
              ? "Not Available"
              : (data.abstract_text || data.abstract)}
          </p>
        </div>
      )}

      {summaryHoverInfo && (
        <div
          className="ev-card-4 p-2 ev-radius-small"
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
          style={{
            position: "fixed",
            top: summaryHoverInfo.top ?? "auto",
            bottom: summaryHoverInfo.bottom ?? "auto",
            left: summaryHoverInfo.left,
            width: 380,
            maxHeight: summaryHoverInfo.maxHeight,
            overflowY: "auto",
            zIndex: 9999,
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
          }}
        >
          <div
            className="ev-xsmall-p-dark"
            style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}
          >
            {summaryHoverInfo.summary}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreeningView;